import styled from "@emotion/styled";
import { Flex } from "@radix-ui/themes";
import { observer } from "mobx-react-lite";
import type React from "react";
import { Suspense, lazy, useState } from "react";
import { useTranslation } from "react-i18next";

import { FavoriteMarkets } from "@components/FavoriteMarkets";
import { MarketData } from "@components/MarketData";
import { Tradebox } from "@components/Tradebox";
import { theme } from "@themes/themes";

import { useStores } from "@stores";
import { DIALOG_TYPE } from "@stores/DialogStore";

import { ToggleGroup, ToggleGroupItem } from "@src/ui-kit";
import { Sheet } from "@src/ui-kit/Sheet";

import { TradingViewChart } from "./TradingView";

const Orderbook = lazy(() => import("@components/Orderbook").then((m) => ({ default: m.Orderbook })));
const Trades = lazy(() => import("@components/Trades").then((m) => ({ default: m.Trades })));
const TradeTables = lazy(() => import("./TradeTables/TradeTables").then((m) => ({ default: m.TradeTables })));

type TabValues = "chart" | "orderbook" | "trades";

export const HomeMobileLayout: React.FC = observer(() => {
  const { t } = useTranslation();
  const { dialogStore } = useStores();

  const TABS: { label: string; value: TabValues }[] = [
    { label: "Chart", value: "chart" as const },
    { label: t("orderbook.title"), value: "orderbook" as const },
    { label: t("orderbook.trades"), value: "trades" as const },
  ];

  const [tab, setTab] = useState<TabValues>(TABS[0].value);
  const [mountedTabs, setMountedTabs] = useState<Record<TabValues, boolean>>({
    chart: true,
    orderbook: false,
    trades: false,
  });

  const handleTabChange = (value: typeof tab) => {
    setTab(value);
    setMountedTabs((prev) => (prev[value] ? prev : { ...prev, [value]: true }));
  };

  const handleCloseTradeboxModal = () => {
    dialogStore.close(DIALOG_TYPE.TRADEBOX);
  };

  return (
    <LayoutContainerMobile>
      <HeaderArea>
        <FavoriteMarkets />
        <MarketData />
      </HeaderArea>
      <ContentArea>
        <MainContainer>
          <TabsSection>
            <TabsContainer>
              <ToggleGroup
                ItemComponent={ToggleGroupItemStyled}
                data-direction={tab}
                options={TABS}
                value={tab}
                fullWidth
                onChange={handleTabChange}
              />
            </TabsContainer>
          </TabsSection>
          <ChartContentArea>
            {mountedTabs.chart && (
              <ContentWrapper isVisible={tab === "chart"}>
                <TradingViewChart isContainer={false} />
              </ContentWrapper>
            )}
            {mountedTabs.orderbook && (
              <ContentWrapper isVisible={tab === "orderbook"}>
                <Suspense fallback={null}>
                  <Orderbook />
                </Suspense>
              </ContentWrapper>
            )}
            {mountedTabs.trades && (
              <ContentWrapper isVisible={tab === "trades"}>
                <Suspense fallback={null}>
                  <Trades />
                </Suspense>
              </ContentWrapper>
            )}
          </ChartContentArea>
        </MainContainer>
      </ContentArea>
      <TablesArea>
        <Suspense fallback={<TradeTablesFallback data-testid="trade-tables:fallback-mobile" />}>
          <TradeTables />
        </Suspense>
      </TablesArea>
      <Sheet
        open={dialogStore.isVisible(DIALOG_TYPE.TRADEBOX)}
        fullScreenOnly
        showDragHandle
        onOpenChange={handleCloseTradeboxModal}
      >
        <Tradebox inset={true} insetClose={handleCloseTradeboxModal} />
      </Sheet>
    </LayoutContainerMobile>
  );
});
const LayoutContainerMobile = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: auto auto 1fr auto;
  gap: 6px;

  width: 100%;
  max-width: 100vw;
  height: 100%;
  overflow: hidden;
  padding: 4px;
  box-sizing: border-box;
  background: ${theme.color("gray-1")};
`;

const HeaderArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  overflow: hidden;
`;

const ContentArea = styled.div`
  min-height: 0;
  overflow: hidden;
`;

const TablesArea = styled.div`
  min-height: 200px;
  min-width: 0;
  overflow: hidden;
`;

const TradeTablesFallback = styled.div`
  background: ${theme.color("gray-2")};
  min-height: 200px;
  height: 100%;
  border: 1px solid ${theme.color("gray-6")};
  border-radius: 8px;
`;

const MainContainer = styled(Flex)`
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 400px;
  overflow: hidden;

  background: ${theme.color("gray-2")};

  border-radius: 8px;
  border: 1px solid ${theme.color("gray-6")};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const TabsSection = styled.div`
  flex-shrink: 0;
`;

const TabsContainer = styled(Flex)`
  width: 100%;
  padding: 12px 12px 8px;
  background: ${theme.color("gray-2")};
  border-bottom: 1px solid ${theme.color("gray-5")};
`;

const ChartContentArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow: hidden;
  position: relative;
`;

const ContentWrapper = styled.div<{ isVisible: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  visibility: ${({ isVisible }) => (isVisible ? "visible" : "hidden")};
  pointer-events: ${({ isVisible }) => (isVisible ? "auto" : "none")};
  z-index: ${({ isVisible }) => (isVisible ? 1 : 0)};
`;

const ToggleGroupItemStyled = styled(ToggleGroupItem)`
  ${theme.font.body12};
  height: 32px;
  padding: 0 16px;
  border-radius: 6px;

  &[data-state="on"] {
    color: ${theme.color("accent-contrast")};
  }
`;
